import SubmissionMaintenanceNotice from './components/SubmissionMaintenance';
import {
  useQueryClient,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import styled from 'styled-components';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Redirect,
  Route,
  HashRouter as Router,
  Switch,
  useLocation,
} from 'react-router-dom';
import CompletedPage from './components/CompletedPage';
import CreateNewMarkingCriteriaRoot from './components/CreateNewMarkingCriteria/CreateNewMarkingCriteriaRoot';
import ExemplarResponsesPage from './components/ExemplarResponsesPage';
const FeedbacksRoot = React.lazy(() =>
  import('./components/FeedbacksComponents/FeedbacksRoot')
);
const SubmissionViewWrapper = React.lazy(() =>
  import('./components/SubmissionView/SubmissionViewWrapper')
);
const CreateNewTask = React.lazy(() => import('./components/CreateNewTask'));
// Phone upload handoff screen; only fetched when a phone opens #/m/upload/<token>.
const MobilePhoneUpload = React.lazy(() =>
  import('./components-mobile/MobilePhoneUpload')
);
const TeacherClassesRoot = React.lazy(() =>
  import('./components/Classes/TeacherClassesRoot')
);
const DraftWizardPage = React.lazy(
  () => import('./components/BulkUpload/DraftWizardPage'),
);
const TasksStudentUploadRedirect = React.lazy(
  () =>
    import(
      './components/TeacherResponseUpload/TasksStudentUploadRedirect'
    ),
);
// Teacher Loop (build flag REACT_APP_TEACHER_LOOP=on only): the public Teacher
// Check pages. Routed only with the flag on. Every Teacher Loop import() in
// this file sits behind the flag literal, so a flag-off build drops it and its
// chunk entirely (utils/teacherLoopGate.js).
const TeacherCheckPage = React.lazy(() =>
  process.env.REACT_APP_TEACHER_LOOP === 'on'
    ? import('./components/TeacherLoop/teacherCheck/TeacherCheckPage')
    : noTeacherLoopModule()
);
const TeacherCheckVerifyPage = React.lazy(() =>
  process.env.REACT_APP_TEACHER_LOOP === 'on'
    ? import('./components/TeacherLoop/teacherCheck/TeacherCheckVerifyPage')
    : noTeacherLoopModule()
);
// Gift your teacher: the thank-you card and its confirmation link (flag on only).
const GiftCardPage = React.lazy(() =>
  process.env.REACT_APP_TEACHER_LOOP === 'on'
    ? import('./components/TeacherLoop/gift/GiftCardPage')
    : noTeacherLoopModule()
);
const GiftVerifyPage = React.lazy(() =>
  process.env.REACT_APP_TEACHER_LOOP === 'on'
    ? import('./components/TeacherLoop/gift/GiftVerifyPage')
    : noTeacherLoopModule()
);
import PageNotFound from './components/PageNotFound';
import ConnectionBlocked from './components/ConnectionBlocked';
import AccountSettingsRoot from './components/Settings/AccountSettingRoot';
import TaskDetail from './components/StartAssignment/TaskDetail';
import {
  getUserName,
  getUserRole,
  getAuthToken,
  getUserId,
  setProfileCookies,
  deleteProfileCookies,
  isStoredJwtExpired,
} from './userLocalDetails';
import NewDocPage from './components/NewDocRoot';
import MainSidebar from './components/MainSidebar';
import CommentBanks from './components/CommentBanks';
import CommentBankFeedbackAreas from './components/CommentBanks/CommentBankFeedbackAreas';
import CreateNewCommentBankRoot from './components/CommentBanks/CreateNewCommentBank';
import SuccessCriteria from './components/SuccessCriteria/index';
import SuccessCriteriaDetail from './components/SuccessCriteria/SuccessCriteriaDetail';
import CreateNewSuccessCriteria from './components/SuccessCriteria/CreateNewSuccessCriteria';
import ShortAnswers from './components/ShortAnswers';
import ShortAnswerDetail from './components/ShortAnswers/ShortAnswerDetail';
import CreateNewShortAnswer from './components/ShortAnswers/CreateNewShortAnswer';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  DesktopRouteCanonicalizer,
  useMobileHandoffView,
} from './components/ReactiveRender';
import WelcomeOverlayMobile from './components2/WelcomeOverlayMobile';
import { AppMobileGate } from './components-mobile/MobileGate';
import MobileMuiLayer from './components-mobile/MobileMuiLayer';
import { isMobileExperienceFlagOn } from './components-mobile/useMobileExperience';
import { phoneUploadTokenFromHash } from './service/phoneUpload';
import ClassSubjectsModal from './components/Lti/ClassSubjectsModal';
import { isUsCountry } from './utils/lexicon';
import SharedTasks from './components/SharedTasks';
import SharedMarkingTools from './components/SharedMarkingTools';
import AdvancedMarkingControls from './components/AdvancedMarkingControls';
import VisibilityWrapper from './components2/VisibilityWrapper/VisibilityWrapper';
import VerifyClassesPopup from './components2/VerifyClassesPopup';

import {
  exchangeCodeForToken,
  exchangeLtiCodeForToken,
  getProfile,
  isLoggedOut,
  updateLastOnboardingTime,
  markOnboardingCompleted,
  profileStateYear,
  saveSessionBooking,
} from './service';
import Dashboard from './components/Dashboard';
import ClassInsightsPage from './components/ClassInsights/ClassInsightsPage';
import Tasks from './components/Tasks';
import queryString from 'query-string';
import { ddRum } from './dd';
import { getLocalStorage } from './utils/function';
import OnboardingScreen from './components2/Onboard/OnboardingScreen';
import {
  shouldRequireMyDraftsStateSelection,
  shouldRequireNonSchoolTeacherStateYearSelection,
} from './components2/Onboard/rules';
// PageSkeleton retained for component-level use; the app-level loader is the
// branded Lottie Loader so users see "Loading brain power..." throughout boot
// and lazy-chunk fetches, not the generic shimmer that flashed first.
import { PageSkeleton } from './components/Skeleton';
import Loader from './components/Loader';
import TeacherOnboarding from './components2/TeacherOnboarding';
import OnboardingComplete from './components2/TeacherOnboarding/OnboardingComplete';
import SessionBookingPopup from './components2/SessionBooking';
import CustomizeDraftFeedback from './components-onboarding/CreateNewTask/components/DraftFeedback/index.jsx';
import {
  isShowWelcomeOnboarding,
  shouldShowSidebar,
  isStudentOnboarding,
  isTeacherOnboarding,
} from './rules';
import { AppContext } from './app.context';
// Only the dependency-free flag module and the tiny route guard load with the
// app; the claim runner and the gift dialog are lazy, fetched only when the
// flag is on and they render (fallback null: nothing to show while loading).
// The flag is tested as the literal below, never isTeacherLoopEnabled(), so a
// flag-off build drops the guarded code (utils/teacherLoopGate.js).
import {
  isTeacherLoopStandalonePath,
  noTeacherLoopModule,
  teacherLoopPublicPaths,
} from './utils/teacherLoopGate';
import HideOnTeacherCheckRoute from './components/TeacherLoop/HideOnTeacherCheckRoute';
const TeacherLoopPendingClaims = React.lazy(() =>
  process.env.REACT_APP_TEACHER_LOOP === 'on'
    ? import('./components/TeacherLoop/TeacherLoopPendingClaims')
    : noTeacherLoopModule()
);
const TeacherLoopPendingGift = React.lazy(() =>
  process.env.REACT_APP_TEACHER_LOOP === 'on'
    ? import('./components/TeacherLoop/gift/TeacherLoopPendingGift')
    : noTeacherLoopModule()
);
import WelcomeOnboarding from './components2/TeacherOnboarding/WelcomeOnboarding';
import SimpleOnboardingCarousel from './components2/TeacherOnboarding/SimpleOnboardingCarousel';
import TeacherPlanUpgradePopup from './components2/freemium/TeacherPlanUpgradePopup';
import StudentPlanUpgradePopup from './components2/freemium/StudentPlanUpgradePopup';
import Cookies from 'js-cookie';
import {
  useActiveClassData,
  useProfile,
  useMarkingCriterias,
  useCommentBanks,
  useSuccessCriteria,
  useShortAnswers,
} from './components/state/hooks';
import usePrefetchDashboard from './hooks/usePrefetchDashboard';
import { externalIDPUrl, exchangeSsoToken } from './utils/login';
import FeedbackProvider from './components/GiveFeedback/FeedbackProvider';
import FeedbackHistory from './components/GiveFeedback/FeedbackHistory';
import {
  canAccessTasks,
  canAccessJeddAI,
  canAccessInsights,
  canAccessMarkingTools,
  canViewCurrentTasks,
  canViewClosedTasks,
  canViewFeedbackRequests,
  canViewSharedResponses,
  canViewFeedbackHistory,
  isNonSchoolStudentRole,
  isNonSchoolTeacherRole,
  isExpertRole,
  canCreateTask,
  isSchoolStudentRole,
  isSchoolTeacherRole,
  isNewUserProfile,
} from './utils/userAccessHelper.js';
import ClassChangeRequestPopup from './components2/ClassChangeRequest/index';
import MarkingPreferencesPopup from './components/MarkingPreferencesPopup';
import { TourContext, initialState, tourReducer, useTour } from './TourContext';
import { resolveProfileStates } from './utils/function';
import {
  isEmbeddedMode,
  getLtiCodeFromUrl,
  markEmbeddedSession,
  clearEmbeddedSession,
  rememberLaunchContext,
  clearLaunchContext,
  isEmbeddedSessionExpired,
  onEmbeddedSessionExpired,
} from './utils/ltiEmbedded';
import { StateSelectionContext } from './hooks/useUserStates';

// Onboarding mirror entry points — stripped/demo-only clones of production pages
// that carry the tour overlays. Lets us keep `components/` tour-free.
// MarkingPage points at AccountSettingRoot (not the child Deskt component) because
// the child requires ~20 props normally supplied by the Root.
// Both /onboarding/documents/:id and /onboarding/submissions/:id render
// OnboardingFeedbacksRoot — the mirror switches demo content by URL :id.
// The six onboarding route pages are code-split: only school teachers
// mid-onboarding ever render them, so they load as async chunks
// (preloaded when the setup checklist opens). See components2/OnboardingLazy.
import {
  OnboardingMarkingPage,
  OnboardingCreateNewTask,
  OnboardingFeedbacksRoot,
  OnboardingCommentBanks,
  OnboardingSuccessCriteria,
  OnboardingShortAnswers,
  OnboardingPage,
  preloadOnboardingPages,
} from './components2/OnboardingLazy';
// Onboarding sidebar — same shape as the production MainSidebar but carries
// tour overlays. Rendered on /onboarding/* paths only via the SidebarSwitcher
// below so production layouts stay tour-free.
import OnboardingMainSidebar from './components-onboarding/MainSidebar';
// Sandbox guards + UI for the tour flow. TourGuard prevents the user from
// navigating outside /onboarding/* while a tour is active. The progress
// button is the only way back into the checklist after the user has
// confirmed a tour exit.
import TourGuard from './components-onboarding/TourGuard';
import StagingModelPicker from './components2/StagingModelPicker';

/**
 * Picks the right MainSidebar based on the current route. Inside HashRouter
 * via useLocation so the swap is reactive. Production sidebar everywhere
 * except `/onboarding/*`.
 */
function SidebarSwitcher({ isSchoolTeacherProfile }) {
  const location = useLocation();
  const isOnboardingRoute = location.pathname.startsWith('/onboarding/');
  if (isOnboardingRoute) {
    return (
      <OnboardingMainSidebar isSchoolTeacherProfile={isSchoolTeacherProfile} />
    );
  }
  return <MainSidebar isSchoolTeacherProfile={isSchoolTeacherProfile} />;
}

/**
 * Derive the post-exchange redirect path from the current HashRouter URL when the
 * launch carried no explicit `?redirect=`. The LMS launch shape is
 * `?...#/tasks/:id/start?lti_code=…&lctx=…`; the route we must preserve lives in
 * the hash. Strip the LTI-only params (`lti_code`, `lctx`) and return a
 * `/#<route>` path so the assignment route survives the success-handler's
 * `replaceState(..., '/')` and TaskDetail still mounts. Returns null when there is
 * no meaningful hash route (e.g. a bare `?lti_code=` on the search string), in
 * which case the caller leaves the destination untouched (unchanged behaviour).
 */
const deriveHashRedirectPath = () => {
  try {
    const hash = window.location.hash || '';
    // Strip the leading '#'.
    const raw = hash.startsWith('#') ? hash.slice(1) : hash;
    if (!raw || raw === '/') return null;
    const qIndex = raw.indexOf('?');
    const path = qIndex === -1 ? raw : raw.slice(0, qIndex);
    if (!path || path === '/') return null;
    let query = '';
    if (qIndex !== -1) {
      const params = new URLSearchParams(raw.slice(qIndex + 1));
      params.delete('lti_code');
      params.delete('lctx');
      const remaining = params.toString();
      if (remaining) query = `?${remaining}`;
    }
    return `/#${path}${query}`;
  } catch (e) {
    return null;
  }
};

function App() {
  const queryClient = useQueryClient();
  // Teacher Loop adds '/check/' (the Teacher Check pages) with its build flag
  // on; with the flag off this list is exactly ['/view/submissions', '/404'].
  const publicPaths = [
    '/view/submissions',
    '/404',
    ...(process.env.REACT_APP_TEACHER_LOOP === 'on'
      ? teacherLoopPublicPaths()
      : []),
  ];
  const currentPath = window.location.hash
    ? window.location.hash.substring(1).split('?')[0]
    : window.location.pathname;

  // Phone upload handoff (#/m/upload/<token>): opened by scanning the desktop's
  // QR code, usually on a phone that is not signed in. The token in the link is
  // its only credential, so the route is public — but ONLY while the mobile
  // experience flag is on (flag off: null, and nothing below changes). Read
  // once on mount, so an onboarding redirect on a signed-in phone cannot pull
  // the screen out from under the student mid-upload.
  const [phoneUploadToken] = useState(() =>
    isMobileExperienceFlagOn()
      ? phoneUploadTokenFromHash(window.location.hash)
      : null
  );
  const isPublicPath =
    publicPaths.some((path) => currentPath.startsWith(path)) ||
    Boolean(phoneUploadToken);
  const exchangeInProgress = useRef(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAuthBootstrapDone, setIsAuthBootstrapDone] = useState(false);
  const [authBootstrapError, setAuthBootstrapError] = useState(null);
  const [showStudentOnboarding, setShowStudentOnboarding] = useState(false);
  const [showNonSchoolTeacherOnboarding, setShowNonSchoolTeacherOnboarding] =
    useState(false);
  const [showTeacherOnboarding, setShowTeacherOnboarding] = useState(false);
  const [showWelcomeOnboarding, setShowWelcomeOnboarding] = useState(false);
  const [showStateSelectionModal, setShowStateSelectionModal] = useState(false);
  const [sidebarOverlayCount, setSidebarOverlayCount] = useState(0);
  // Embedded (LMS iframe) session-expired flag. Flipped by the pub/sub signal that
  // service.js / service/api.js fire on a 401 while embedded, so the app can render
  // the "relaunch from your LMS" state instead of wiping storage / navigating.
  const [embeddedExpired, setEmbeddedExpired] = useState(
    isEmbeddedSessionExpired()
  );
  const apiBaseUrl =
    process.env.REACT_APP_API_BASE_URL ?? 'http://localhost:8080';
  const activateSidebarOverlay = () => {
    setSidebarOverlayCount((prev) => prev + 1);
  };
  const deactivateSidebarOverlay = () => {
    setSidebarOverlayCount((prev) => (prev > 0 ? prev - 1 : 0));
  };
  const isSidebarOverlayActive = sidebarOverlayCount > 0;

  const {
    showOnboardingComplete,
    showVerifyClassesPopup,
    showSessionBooking,
    markingTourActive,
    newTaskTourActive,
    newTaskTourStep,
    onboardingTasks,
    completedTours,
    draftTourActive,
    draftTourStep,
    markingTourStep,
    feedbackTourActive,
    feedbackTourStep,
    showDraftFeedback,
    onboardingProgress,
    hasLoadedUserPersistence,
    setShowOnboardingComplete,
    setShowVerifyClassesPopup,
    setShowSessionBooking,
    setMarkingTourActive,
    setNewTaskTourActive,
    setNewTaskTourStep,
    setOnboardingTasks,
    setCompletedTours,
    setDraftTourActive,
    setDraftTourStep,
    setMarkingTourStep,
    setFeedbackTourActive,
    setFeedbackTourStep,
    setShowDraftFeedback,
    setOnboardingProgress,
    markTourAsComplete,
    tourRelaunchRequested,
    wizardTourActive,
    wizardCompleted,
    setWizardCompleted,
    setWizardAudience,
    launchTour,
  } = useTour();

  const [showClassChangeRequest, setShowClassChangeRequest] = useState(false);
  const [showMarkingPreferencesPopup, setShowMarkingPreferencesPopup] =
    useState(false);
  // Simplified onboarding flow (non-school teacher + school/non-school student):
  // [collect] → welcome carousel → demo "My Drafts" wizard → plans → My Drafts.
  const [showSimpleCarousel, setShowSimpleCarousel] = useState(false);
  const [showSimplePlans, setShowSimplePlans] = useState(false);
  // 'nonSchoolTeacher' | 'nonSchoolStudent' | 'schoolStudent'
  const [simpleAudience, setSimpleAudience] = useState(null);
  // One-shot guard so the auto-show effects don't re-fire once the flow has
  // started (we navigate by hash, not hard reload, so the profile cache can
  // still read hasCompletedOnboarding=false mid-flow).
  const enteredSimpleFlowRef = useRef(false);

  const {
    data: profile,
    isLoadingdata: isLoadingProfile,
    resetData: refetchProfile,
  } = useProfile(null, isAuthenticated);
  const isSchoolTeacherProfile = isSchoolTeacherRole(profile);
  // Setup-progress onboarding surface (carousel, checklist, ring, popups,
  // /onboarding mirror routes) is reserved for new users — see isNewUserProfile.
  const isNewSchoolTeacher =
    isSchoolTeacherProfile && isNewUserProfile(profile);
  // Render gate for the onboarding *surface* (setup checklist + the
  // /onboarding/* mirror tour routes + the draft-feedback customiser). New
  // school teachers always qualify; ANY school teacher who explicitly
  // re-launches the tour from the Help Centre qualifies for that session
  // (in-memory only — never re-persisted), so finished / pre-tour teachers can
  // re-watch. The AUTO-show effects intentionally keep gating on
  // isNewUserProfile, so the surface NEVER auto-pops for a returning teacher —
  // it only appears on an explicit relaunch.
  const canShowOnboardingSurface =
    isSchoolTeacherProfile &&
    (isNewUserProfile(profile) || tourRelaunchRequested);
  // The simplified wizard reuses the demo "My Drafts" mirror route. Allow the
  // three simple audiences onto it while their wizard is active (the surface
  // gate above is school-teacher-only). Also keep it open for the one render
  // after Finish (wizardCompleted, before the wizard-complete effect navigates
  // away) so the route doesn't bounce to firstAccessibleRoute mid-transition.
  const canShowWizardDemo = Boolean(wizardTourActive || wizardCompleted);

  const {
    data: classes,
    isLoadingdata: isLoadingClasses,
    resetData: refetchClasses,
  } = useActiveClassData(null, isAuthenticated);

  // Prefetch tasks, notifications, assignments, and documents in parallel
  // as soon as auth completes — eliminates waterfall on first login
  usePrefetchDashboard(isAuthenticated);

  // Preload everything the marking tour will need as soon as we know we're a
  // school teacher in onboarding. Each mirror sub-page (Rubrics, Comment
  // Banks, Success Criteria, Short Answers — tour steps 3-7) seeds its
  // local state from the corresponding React Query cache entry, so by the
  // time the user clicks "View marking templates" the cache is warm and
  // every sub-tool page renders real content with no fetch flash. The
  // hooks' own guards return [] for non-teachers; the condition just
  // suppresses the network call entirely for users who don't need it.
  // staleTime is 1h so a single fetch per resource covers the whole
  // welcome → personalise → checklist → tour walk.
  const shouldPreloadMarkingTour =
    isAuthenticated &&
    isSchoolTeacherProfile &&
    !profile?.hasCompletedOnboarding;
  useMarkingCriterias(null, shouldPreloadMarkingTour);
  useCommentBanks(null, shouldPreloadMarkingTour);
  useSuccessCriteria(null, shouldPreloadMarkingTour);
  useShortAnswers(null, shouldPreloadMarkingTour);

  // Warm the code-split onboarding page chunks the moment the checklist is
  // visible — the checklist is the only gateway into the tours, so by the
  // time a row is clicked the chunk is cached and the Suspense fallback
  // never shows.
  useEffect(() => {
    if (showOnboardingComplete) preloadOnboardingPages();
  }, [showOnboardingComplete]);

  useEffect(() => {
    if (isLoggedOut) {
      setIsAuthBootstrapDone(true);
      return;
    }
    const token = getAuthToken();
    const userId = getUserId();
    const parsedFromSearch = queryString.parse(window.location.search);
    const parsedFromHash = window.location.hash.includes('?')
      ? queryString.parse(window.location.hash.split('?')[1])
      : {};
    const parsed = { ...parsedFromHash, ...parsedFromSearch };

    // New SSO flow: handle sso_token from WordPress SSO
    if (parsed.sso_token) {
      if (exchangeInProgress.current) {
        return;
      }
      exchangeInProgress.current = true;
      // Never log the SSO token itself — it's a short-lived credential.

      exchangeSsoToken(parsed.sso_token)
        .then((data) => {
          // Store the session data
          // Don't log the response body — it can contain auth material.
          // setProfileCookies must run BEFORE queryClient.clear() — see the
          // matching comment in the legacy `parsed.code` branch below for
          // why the order matters.
          setProfileCookies(data);
          queryClient.clear();
          setIsAuthenticated(true);
          setAuthBootstrapError(null);
          window.history.replaceState({}, document.title, '/');
          exchangeInProgress.current = false;
          setIsAuthBootstrapDone(true);
          if (parsed.redirect) {
            localStorage.setItem('redirectPath', `/#${parsed.redirect}`);
          }
        })
        .catch((error) => {
          console.error('Error exchanging SSO token:', error);
          exchangeInProgress.current = false;
          setAuthBootstrapError(
            `SSO token exchange failed against ${apiBaseUrl}`
          );
          setIsAuthBootstrapDone(true);
          // Redirect to login on SSO error
          const isLocalhost =
            window.location.hostname === 'localhost' ||
            window.location.hostname === '127.0.0.1';
          if (!isLocalhost && !isEmbeddedMode()) {
            const authUrl = externalIDPUrl();
            if (authUrl) window.location.href = authUrl;
          }
        });
    }
    // LTI 1.3 launch: exchange the one-time lti_code for an app session.
    // Mirrors the ?code= branch below; differs only in: marks the session
    // embedded, remembers the lctx launch-context token for POST /submissions,
    // and (via embedded mode) forces the JWT into localStorage.
    //
    // Gate on parsed.lti_code (consistent with the parsed.code / parsed.sso_token
    // siblings in this chain), but read the actual code through the helper so the
    // hash+search parsing lives in exactly ONE place (utils/ltiEmbedded). On a
    // HashRouter URL (?...#/tasks/x/start?lti_code=…) parsed.lti_code may be empty
    // while getLtiCodeFromUrl() still finds it in the hash query, so prefer the
    // helper and fall back to parsed.lti_code.
    else if (parsed.lti_code || getLtiCodeFromUrl()) {
      const ltiCode = getLtiCodeFromUrl() || parsed.lti_code;
      if (exchangeInProgress.current) {
        return;
      }
      exchangeInProgress.current = true;
      markEmbeddedSession();
      clearLaunchContext();
      if (parsed.lctx) {
        rememberLaunchContext(parsed.lctx);
      }

      // Capture the destination BEFORE the success handler strips the URL.
      // An explicit ?redirect= wins (matches the ?code= / ?sso_token= siblings).
      // Otherwise, for the HashRouter launch shape (?...#/tasks/:id/start?lti_code=…)
      // the route lives in the hash; derive it (minus the LTI params) so the
      // assignment route survives the replaceState and TaskDetail still mounts.
      const ltiRedirectPath =
        parsed.redirect != null
          ? `/#${parsed.redirect}`
          : deriveHashRedirectPath();

      exchangeLtiCodeForToken(ltiCode)
        .then((data) => {
          // GET /lti/exchange returns only { token }; persist it (sets the auth token),
          // then load the FULL profile so name/userId/role/permissions are populated too
          // — the { token, userId, name, role, permissions } shape the ?code/?sso flows
          // pass to setProfileCookies in one call. Without this the missing fields land
          // in localStorage as "undefined" and crash the render path on the next read.
          setProfileCookies(data);
          return getProfile();
        })
        .then((profile) => {
          if (!profile) throw new Error('LTI profile unavailable');
          setProfileCookies(profile);
          // A parent profile observer can still hold the previous LMS user's
          // data (or an in-flight request). Cancel/remove it and seed the identity
          // just verified with the new token before any authenticated UI mounts.
          queryClient.clear();
          queryClient.setQueryData(['profile'], profile);
          // Set the destination before HashRouter mounts, so neither its default
          // route nor a previous login's redirect can consume this launch.
          localStorage.removeItem('redirectPath');
          window.history.replaceState({}, document.title, ltiRedirectPath || '/');
          setIsAuthenticated(true);
          setAuthBootstrapError(null);
          exchangeInProgress.current = false;
          setIsAuthBootstrapDone(true);
        })
        .catch((error) => {
          console.error('Error exchanging LTI code:', error);
          exchangeInProgress.current = false;
          // Exchange failed: drop the embedded markers so this tab doesn't stay
          // sticky-embedded and alter later top-level auth / 401 behaviour.
          clearEmbeddedSession();
          clearLaunchContext();
          setAuthBootstrapError(
            `LTI code exchange failed against ${apiBaseUrl}.`
          );
          setIsAuthBootstrapDone(true);
          // Do NOT redirect to the IdP here — we are embedded in an LMS frame.
        });
    }
    // Legacy flow: handle code from miniOrange OAuth
    else if (parsed.code) {
      if (exchangeInProgress.current) {
        return;
      }
      exchangeInProgress.current = true;

      // Use the API client to exchange the code
      exchangeCodeForToken(parsed.code)
        .then((data) => {
          // Don't log the response body — it can contain auth material.
          // setProfileCookies must run BEFORE queryClient.clear() — clear()
          // triggers refetches of any active observer queries synchronously,
          // and getProfile checks isLoggedIn (localStorage userId) before
          // hitting the API. Without the token in storage first, the refetch
          // caches `null` and never re-runs (refetchOnMount=false), starving
          // downstream consumers like TourProvider's setupChecklist load.
          setProfileCookies(data);
          queryClient.clear();
          setIsAuthenticated(true);
          setAuthBootstrapError(null);
          window.history.replaceState({}, document.title, '/');
          exchangeInProgress.current = false;
          setIsAuthBootstrapDone(true);
          if (parsed.redirect) {
            localStorage.setItem('redirectPath', `/#${parsed.redirect}`);
          }
        })
        .catch((error) => {
          console.error('Error exchanging code:', error);
          exchangeInProgress.current = false; // Reset the flag on error
          setAuthBootstrapError(
            `Code exchange failed against ${apiBaseUrl}. Check backend and API base URL.`
          );
          setIsAuthBootstrapDone(true);
        });
    } else if (!token && !userId) {
      if (
        !isPublicPath &&
        !parsed.code &&
        !parsed.sso_token &&
        !parsed.lti_code &&
        !getLtiCodeFromUrl()
      ) {
        // Redirect to IDP - don't set isAuthBootstrapDone so Loader stays
        // visible and prevents routes from rendering (which would fire API
        // calls that 401 and override this redirect).
        const isLocalhost =
          window.location.hostname === 'localhost' ||
          window.location.hostname === '127.0.0.1';
        if (!isLocalhost && !isEmbeddedMode()) {
          // Non-embedded, non-localhost, logged out → redirect to the WP IdP (unchanged).
          const authUrl = externalIDPUrl();
          if (authUrl) {
            window.location.href = authUrl;
          }
        } else {
          // Either embedded (IdP won't render in a frame → show the relaunch state)
          // OR localhost (no IdP redirect in local dev). Both just stop the loader.
          setIsAuthBootstrapDone(true);
        }
      } else {
        setIsAuthBootstrapDone(true);
      }
    } else if (isStoredJwtExpired()) {
      // Stored JWT is provably past its exp (localStorage-JWT mode only —
      // cookie-auth hosts never reach here). Without this check a returning
      // user's stale token was presented as valid: the dashboard prefetch
      // burst fired ~5-10 doomed requests, every one 401'd, and service.js
      // wiped storage with a hard redirect mid-render. Clear the dead session
      // up front and route to the IdP like the logged-out branch above.
      deleteProfileCookies();
      const isLocalhost =
        window.location.hostname === 'localhost' ||
        window.location.hostname === '127.0.0.1';
      if (!isLocalhost && !isEmbeddedMode() && !isPublicPath) {
        const authUrl = externalIDPUrl();
        if (authUrl) {
          // Keep the loader up (isAuthBootstrapDone stays false) so routes
          // don't mount and fire API calls that race the redirect.
          window.location.href = authUrl;
          return;
        }
      }
      // Public path, embedded, or localhost: render unauthenticated instead.
      setIsAuthBootstrapDone(true);
    } else {
      // Token already present
      setIsAuthenticated(true);
      setAuthBootstrapError(null);
      setIsAuthBootstrapDone(true);
    }
  }, []);
  useEffect(() => {
    if (!isEmbeddedMode()) return undefined;
    const off = onEmbeddedSessionExpired(() => setEmbeddedExpired(true));
    return off;
  }, []);
  useEffect(() => {
    if (isEmbeddedMode()) return;
    if (isAuthenticated && profile && !isLoadingProfile) {
      const defaultShowStudentOnboarding =
        isNewUserProfile(profile) &&
        isNonSchoolStudentRole(profile) &&
        canAccessJeddAI(profile) &&
        !profile?.hasCompletedOnboarding &&
        (!resolveProfileStates(profile) || !profile?.year) &&
        !showStudentOnboarding &&
        !enteredSimpleFlowRef.current;

      if (defaultShowStudentOnboarding) {
        setShowStudentOnboarding(true);
      }
    }
  }, [isAuthenticated, isLoadingProfile, profile, showStudentOnboarding]);

  useEffect(() => {
    if (isEmbeddedMode()) return;
    if (isAuthenticated && profile && !isLoadingProfile) {
      const defaultShowNonSchoolTeacherOnboarding =
        isNewUserProfile(profile) &&
        isNonSchoolTeacherRole(profile) &&
        canAccessJeddAI(profile) &&
        !profile?.hasCompletedOnboarding &&
        !resolveProfileStates(profile) &&
        !showNonSchoolTeacherOnboarding &&
        !enteredSimpleFlowRef.current;

      if (defaultShowNonSchoolTeacherOnboarding) {
        setShowNonSchoolTeacherOnboarding(true);
      }
    }
  }, [
    isAuthenticated,
    isLoadingProfile,
    profile,
    showNonSchoolTeacherOnboarding,
  ]);

  // School students get the simplified flow with NO state collection (their
  // state is auto-derived from class membership) and NO plans upsell — they go
  // welcome carousel → demo wizard → My Drafts.
  useEffect(() => {
    if (isEmbeddedMode()) return;
    if (!isAuthenticated || !profile || isLoadingProfile) return;
    // Don't re-enter once started, and never re-stack the carousel on top of an
    // already-active wizard (e.g. a transient state blip).
    if (enteredSimpleFlowRef.current || wizardTourActive) return;
    const shouldShow =
      isNewUserProfile(profile) &&
      isSchoolStudentRole(profile) &&
      canAccessJeddAI(profile) &&
      !profile?.hasCompletedOnboarding;
    if (shouldShow) {
      enteredSimpleFlowRef.current = true;
      // Mark complete up-front (as the non-school audiences do at the collection
      // step) so a mid-flow refresh lands them in the app instead of re-nagging.
      markOnboardingCompleted().catch((e) =>
        console.error('Failed to mark school-student onboarding complete', e)
      );
      setSimpleAudience('schoolStudent');
      setShowSimpleCarousel(true);
    }
  }, [isAuthenticated, isLoadingProfile, profile, wizardTourActive]);

  // End of the simplified wizard (the user hit Finish or Skip on the demo
  // tour): teachers + non-school students see the plans modal; school students
  // go straight to My Drafts. wizardCompleted is a transient TourContext flag.
  // The tour is already inactive here (TourTooltip's Finish/Skip called
  // finishCurrentTour before setting this flag), so we just navigate. The flag
  // reset is deferred one tick so canShowWizardDemo (which includes
  // wizardCompleted) keeps the demo route mounted until we've navigated off it.
  useEffect(() => {
    if (isEmbeddedMode()) return;
    if (!wizardCompleted) return;
    if (simpleAudience === 'schoolStudent') {
      // markOnboardingCompleted already ran when the carousel was first shown.
      setSimpleAudience(null);
      window.location.hash = '#/docs';
    } else {
      setShowSimplePlans(true);
      window.location.hash = `#${firstAccessibleRoute}`;
    }
    const t = setTimeout(() => setWizardCompleted(false), 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wizardCompleted, simpleAudience]);

  // Defensive: reset the one-shot simple-flow guard when the active user
  // changes (logout normally hard-reloads + remounts App, but don't rely on it).
  useEffect(() => {
    enteredSimpleFlowRef.current = false;
  }, [profile?.userId]);

  // Auto-derive states for school users who have no states on their profile
  // Uses user-scoped class data (not global taxonomy) for correct state derivation
  const hasAutoSavedStatesRef = useRef(false);
  useEffect(() => {
    if (!isAuthenticated || !profile || isLoadingProfile || isLoadingClasses)
      return;
    if (hasAutoSavedStatesRef.current) return;
    if (resolveProfileStates(profile)) return;
    const isSchoolUser =
      isSchoolStudentRole(profile) || isSchoolTeacherRole(profile);
    if (!isSchoolUser) return;

    const classList = Array.isArray(classes) ? classes : [];
    // Only a SINGLE-state subject pins a class to a jurisdiction (AU-NSW-*, NZ-*).
    // A multi-state list is the subject's availability footprint — every US subject
    // lists all 51 codes — and writing it to the profile poisons the state axis
    // (states[0]="AK") and permanently blocks the backend's WP-claims adoption
    // (mirrors UserService.jurisdictionOf on the backend).
    const allStates = [
      ...new Set(
        classList
          .flatMap((cls) =>
            (cls.subjects || []).map((s) => (s.states || []).filter(Boolean))
          )
          .filter((states) => states.length === 1)
          .map((states) => states[0])
      ),
    ];
    if (allStates.length === 0) return;

    hasAutoSavedStatesRef.current = true;
    profileStateYear({ states: allStates })
      .then(() => refetchProfile())
      .catch(() => {
        hasAutoSavedStatesRef.current = false;
      });
  }, [isAuthenticated, profile, isLoadingProfile, isLoadingClasses, classes]);

  // Auto-show state selection modal for non-school users who have no states
  // Only fires for users who have already completed onboarding (otherwise the
  // dedicated onboarding routes handle state selection with terms/GTM)
  useEffect(() => {
    if (!isAuthenticated || !profile || isLoadingProfile) return;
    if (resolveProfileStates(profile)) return;
    const isSchoolUser =
      isSchoolStudentRole(profile) || isSchoolTeacherRole(profile);
    if (isSchoolUser) return;
    if (!profile?.hasCompletedOnboarding) return;
    setShowStateSelectionModal(true);
  }, [isAuthenticated, profile, isLoadingProfile]);

  const stateSelectionContextValue = useMemo(
    () => ({
      requestStateSelection: () => setShowStateSelectionModal(true),
    }),
    []
  );

  // Teacher Loop: the teacher onboarding below sends the tab to #/tasks. On
  // one of the Teacher Loop's own pages (/check/…, /thanks/…, /gift/verify)
  // that took the page away from a signed-in teacher before they could act
  // on it, and the confirmation links now wait for a click. So there it waits
  // until the teacher leaves the page: a hashchange to any other route bumps
  // this and the effect runs again. Flag off: the wait never starts, this
  // stays 0 and the effect runs exactly as before.
  const [teacherLoopPageLeft, setTeacherLoopPageLeft] = useState(0);
  // True while that wait is armed. The teacher onboarding is then due but not
  // shown, so showTeacherOnboarding is still false where it used to be true
  // already. The pending-gift prompt reads this as one of its blockers: else
  // a teacher who leaves by an in-app link (history.push renders the new
  // route before the hashchange that ends the wait) got the gift prompt on
  // top of the onboarding. It is cleared in the same effect run that shows
  // the onboarding, so the prompt never sees a gap. Flag off: always false.
  const [teacherLoopOnboardingWaiting, setTeacherLoopOnboardingWaiting] =
    useState(false);

  useEffect(() => {
    let stopWaitingForTeacherLoopPage;
    if (
      !isEmbeddedMode() &&
      isAuthenticated &&
      profile &&
      !isLoadingProfile &&
      hasLoadedUserPersistence
    ) {
      const isTeacher = getUserRole() === 'TEACHER';
      const sevenDaysAgoISO = new Date(
        Date.now() - 7 * 24 * 60 * 60 * 1000
      ).toISOString();
      const lastOnboardingTimeISO =
        profile?.lastOnboardingTime || '1970-01-01T00:00:00.000Z';

      // School teachers only enter the setup-progress flow if their profile
      // was created after the feature launched (profile.createdAt present) —
      // existing users never see the carousel/checklist. Non-school teachers
      // are EXCLUDED here — they get the simplified flow (collect -> carousel ->
      // wizard -> plans) via showNonSchoolTeacherOnboarding instead. Without the
      // exclusion this legacy branch sets showTeacherOnboarding and, because the
      // SPA continuation no longer hard-reloads, the stale flag later redirects
      // them to /teacher-onboarding mid-flow.
      if (
        isTeacher &&
        (!isSchoolTeacherProfile || isNewUserProfile(profile)) &&
        !isNonSchoolTeacherRole(profile) &&
        !profile?.hasCompletedOnboarding &&
        !showTeacherOnboarding &&
        !completedTours.teacherOnboarding &&
        // The mobile app ends desktop tours on mount (finishMobileTakeover),
        // so on a phone this re-fired on every profile refetch and threw the
        // teacher back to #/tasks from wherever they were. Flag off: false.
        !inMobileShell
      ) {
        // The route as publicPaths reads it: the hash route, else the path.
        const onTeacherLoopPage = () =>
          isTeacherLoopStandalonePath(
            window.location.hash
              ? window.location.hash.substring(1).split('?')[0]
              : window.location.pathname
          );
        if (
          process.env.REACT_APP_TEACHER_LOOP === 'on' &&
          onTeacherLoopPage()
        ) {
          const onHashChange = () => {
            if (!onTeacherLoopPage()) {
              setTeacherLoopPageLeft((count) => count + 1);
            }
          };
          window.addEventListener('hashchange', onHashChange);
          setTeacherLoopOnboardingWaiting(true);
          // Runs before the next run of this effect, in the same flush, so a
          // run that shows the onboarding (or waits again) lands in the same
          // render as this reset.
          stopWaitingForTeacherLoopPage = () => {
            window.removeEventListener('hashchange', onHashChange);
            setTeacherLoopOnboardingWaiting(false);
          };
        } else {
          window.location.hash = '#/tasks';
          setShowTeacherOnboarding(true);
        }
      } else if (
        isTeacher &&
        !isNonSchoolTeacherRole(profile) &&
        !showWelcomeOnboarding &&
        !completedTours.welcomeOnboarding
      ) {
        // Non-school teachers have their own dedicated onboarding route; don't
        // also auto-fire the generic welcome overlay on top of it.
        decideAndShowWelcomeOnboarding();
      }
    }
    return stopWaitingForTeacherLoopPage;
  }, [
    isAuthenticated,
    profile,
    isLoadingProfile,
    completedTours,
    hasLoadedUserPersistence,
    teacherLoopPageLeft,
  ]);

  // Auto-show the OnboardingComplete checklist on every login for school
  // teachers (group leaders with at least one group) until they actually
  // dismiss it. Dismissal flips profile.hasCompletedOnboarding via the
  // /users/completedOnboarding PATCH triggered by OnboardingComplete's
  // exit handlers — so on subsequent logins this guard short-circuits.
  //
  // Brand-new teachers see the welcome carousel first (the useEffect
  // above), then closeSchoolTeacherOnboarding hands off to OnboardingComplete.
  // This effect covers the returning-teacher case where the welcome was
  // already done (or completedTours.teacherOnboarding is persisted) but
  // the checklist itself hasn't been dismissed yet.
  useEffect(() => {
    if (
      isEmbeddedMode() ||
      !isAuthenticated ||
      !profile ||
      isLoadingProfile ||
      !hasLoadedUserPersistence
    )
      return;
    if (!isSchoolTeacherProfile) return;
    // Setup checklist is reserved for new users (see isNewUserProfile).
    if (!isNewUserProfile(profile)) return;
    if (profile?.hasCompletedOnboarding) return;
    if (showOnboardingComplete) return;
    if (showTeacherOnboarding) return;
    if (
      showSessionBooking ||
      showVerifyClassesPopup ||
      showMarkingPreferencesPopup
    )
      return;
    // CRITICAL: don't auto-show the checklist while a tour is active. Clicking
    // a tour row in OnboardingComplete activates the tour, navigates to the
    // /onboarding/* mirror, and calls onClose to flip showOnboardingComplete
    // false. Without this guard the effect immediately re-opens the checklist
    // on top of the tour page — the user sees the tour tooltip stacked on
    // top of the checklist instead of the mirror page underneath.
    const anyTourActive =
      markingTourActive ||
      draftTourActive ||
      newTaskTourActive ||
      feedbackTourActive;
    if (anyTourActive) return;
    // Only fire once the welcome flow has been completed — closeSchoolTeacherOnboarding
    // marks 'teacherOnboarding' done and itself flips the checklist on, so
    // racing this useEffect against that handler would double-flip.
    if (!completedTours.teacherOnboarding) return;
    // Need at least one group to make the checklist meaningful — a teacher
    // with no classes can't complete the "verify classes" step.
    const hasGroup = Array.isArray(classes) && classes.length > 0;
    if (!hasGroup) return;
    setShowOnboardingComplete(true);
  }, [
    isAuthenticated,
    profile,
    isLoadingProfile,
    hasLoadedUserPersistence,
    isSchoolTeacherProfile,
    showOnboardingComplete,
    showTeacherOnboarding,
    showSessionBooking,
    showVerifyClassesPopup,
    showMarkingPreferencesPopup,
    markingTourActive,
    draftTourActive,
    newTaskTourActive,
    feedbackTourActive,
    completedTours.teacherOnboarding,
    classes,
  ]);

  //Redirect AFTER login is fully done
  useEffect(() => {
    if (isAuthenticated) {
      const redirectPath = localStorage.getItem('redirectPath');
      if (redirectPath) {
        localStorage.removeItem('redirectPath');

        // alert(`Redirecting to ${redirectPath}`);
        window.location.href = redirectPath; // Full redirect for clean navigation
      }
    }
  }, [isAuthenticated]);
  // Non-school STUDENT finished the state/year collection → continue into the
  // welcome carousel (instead of the old hard reload to '/').
  const closeOnboarding = async () => {
    try {
      await markOnboardingCompleted();
    } catch (error) {
      console.error('Error marking student onboarding as completed:', error);
    }
    localStorage.setItem('showNotificationBar', 'true');
    // Refresh the profile cache so the just-saved state/year (+ completion) is
    // visible to the SPA continuation (the old hard reload did this for free);
    // otherwise renderDraftsPage's state guard re-prompts on /docs.
    try {
      await refetchProfile?.();
    } catch (e) {
      /* non-fatal */
    }
    enteredSimpleFlowRef.current = true;
    setShowStudentOnboarding(false);
    setSimpleAudience('nonSchoolStudent');
    setShowSimpleCarousel(true);
    window.location.hash = `#${firstAccessibleRoute}`;
  };

  // Non-school TEACHER finished the state/usage-goals collection → continue
  // into the welcome carousel.
  const closeNonSchoolTeacherOnboarding = async () => {
    try {
      await markOnboardingCompleted();
    } catch (error) {
      console.error(
        'Error marking non-school teacher onboarding as completed:',
        error
      );
    }
    localStorage.setItem('showNotificationBar', 'true');
    // Refresh the profile cache so the just-saved state/usage-goals (+ completion)
    // is visible to the SPA continuation (the old hard reload did this for free);
    // otherwise the legacy teacher-onboarding effect + renderDraftsPage's state
    // guard re-fire on stale data.
    try {
      await refetchProfile?.();
    } catch (e) {
      /* non-fatal */
    }
    enteredSimpleFlowRef.current = true;
    setShowNonSchoolTeacherOnboarding(false);
    setSimpleAudience('nonSchoolTeacher');
    setShowSimpleCarousel(true);
    window.location.hash = `#${firstAccessibleRoute}`;
  };

  // Welcome carousel finished → launch the demo "My Drafts" wizard.
  const handleSimpleCarouselFinish = () => {
    setShowSimpleCarousel(false);
    setWizardAudience(
      simpleAudience === 'nonSchoolTeacher' ? 'teacher' : 'student'
    );
    launchTour('wizard');
    // TourGuard also redirects here once the wizard is active; be explicit.
    window.location.hash = '#/onboarding/documents/demo-draft?mode=demo';
  };

  // "Continue with free" on the plans modal → My Drafts.
  const finishSimpleOnboarding = () => {
    setShowSimplePlans(false);
    setSimpleAudience(null);
    window.location.hash = '#/docs';
  };

  // Help-Centre "Onboarding" relaunch for the simplified audiences (non-school
  // teacher + school/non-school students): restart at the welcome carousel,
  // skipping the state/year collection (they've already set it). Exposed via
  // AppContext so HelpSidebar can trigger it.
  const startSimpleOnboarding = () => {
    const audience = isNonSchoolTeacherRole(profile)
      ? 'nonSchoolTeacher'
      : isSchoolStudentRole(profile)
      ? 'schoolStudent'
      : 'nonSchoolStudent';
    enteredSimpleFlowRef.current = true;
    setShowSimplePlans(false);
    setSimpleAudience(audience);
    setShowSimpleCarousel(true);
    window.location.hash = `#${firstAccessibleRoute}`;
  };

  const closeSchoolTeacherOnboarding = async () => {
    if (isSchoolTeacherProfile) {
      markTourAsComplete('teacherOnboarding');
      setShowTeacherOnboarding(false);
      setShowOnboardingComplete(true);

      setTimeout(() => {
        setShowSessionBooking(true);
      }, 1000);
    }
  };

  const closeSessionBooking = () => {
    setShowSessionBooking(false);
  };

  const handleCloseOnboardingComplete = () => {
    setShowOnboardingComplete(false);
    // Flow ends here, user sees tasks (the background).
    // Earlier versions wrote dev_showOnboardingComplete / dev_showSessionBooking
    // to localStorage from this path; those were dev-only flags that shipped
    // by accident. The actual state lives in TourContext.
  };
  const decideAndShowWelcomeOnboarding = () => {
    if (showWelcomeOnboarding) return;
    const sevenDaysAgoISO = new Date(
      Date.now() - 7 * 24 * 60 * 60 * 1000
    ).toISOString();
    const lastOnboardingTimeISO =
      profile?.lastOnboardingTime || '1970-01-01T00:00:00.000Z'; // Default to an old date
    if (
      !profile?.disableOnboarding &&
      lastOnboardingTimeISO < sevenDaysAgoISO
    ) {
      setShowWelcomeOnboarding(true);
    }
  };

  const closeWelcomeOnboarding = async () => {
    markTourAsComplete('welcomeOnboarding');
    await updateLastOnboardingTime();
    setShowWelcomeOnboarding(false);
  };

  const mobileExperienceEnabled = isMobileExperienceFlagOn();
  const mobileView = useMobileHandoffView(mobileExperienceEnabled);
  // Teacher Loop (board B2m): /check/<token>, /check/verify and
  // /view/submissions/<id> open the mobile feedback view on a phone even while
  // the mobile experience is dark, so a teacher's first tap on a student's
  // link never dead-ends on the "use desktop" wall. Only those routes: the
  // shell is route-scoped (MobileGate publicOnly) and every other phone route
  // keeps the wall. With the Teacher Loop build flag off mobilePublicRoute is
  // false, so shellEnabled === mobileExperienceEnabled and nothing changes.
  // It reads the hash route on every render, and useMobileHandoffView
  // re-renders App on every hashchange, so the decision follows the route.
  // (Required behind the flag literal: a flag-off build drops the module.)
  const mobilePublicRoute =
    process.env.REACT_APP_TEACHER_LOOP === 'on' &&
    require('./components/ReactiveRender/mobilePublicRoute').isMobilePublicRoute();
  const shellEnabled = mobileExperienceEnabled || mobilePublicRoute;
  const mobilePublicOnly = !mobileExperienceEnabled && mobilePublicRoute;
  // Teacher Loop gift pages (/thanks/<code>, /gift/verify) are responsive
  // pages of their own: on a phone they render in place of the wall (and of
  // the mobile shell). Flag off: always false, so the branch below is as before.
  const responsivePublicRoute =
    process.env.REACT_APP_TEACHER_LOOP === 'on' &&
    require('./components/ReactiveRender/mobilePublicRoute').isResponsivePublicRoute();
  // App-level MUI dialogs sit at z-index ~1300, UNDER the mobile shell
  // (z-index 5000), so on a phone they were invisible. Inside the shell they
  // are portalled into it; the mobile app renders its own verify-classes
  // popup, so the desktop onboarding popups are skipped there. Flag off:
  // inMobileShell is false and nothing below changes.
  const inMobileShell = mobileView && shellEnabled;
  // Teacher Loop (B2m): while a phone shows one of the logged-out pages in the
  // route-scoped shell, onboarding stands down without being changed: no
  // TourGuard redirect or warning, no checklist overlay. The saved tour state
  // is left exactly as it was and resumes once the page is left. Flag off:
  // mobilePublicOnly is false, so this is always false.
  const onboardingSuspended = inMobileShell && mobilePublicOnly;
  const intoShell = (node) =>
    inMobileShell ? <MobileMuiLayer intoShell>{node}</MobileMuiLayer> : node;

  const role = getUserRole();
  const isTeacher = role === 'TEACHER';
  const userName = getUserName();
  userName && (document.title = 'Jeddle - ' + userName);

  // LMS-provisioned classes carry no subject/year (Canvas has no subject field); an embedded
  // teacher must declare them before marking can be curriculum-aligned (ClassSubjectsModal).
  const ltiClassesMissingSubjects = useMemo(
    () =>
      (classes || []).filter(
        (cls) =>
          !cls.isArchived && (!cls.subjects?.length || !cls.years?.length)
      ),
    [classes]
  );

  // Dismissal for the US (non-embedded) class-subjects prompt: once per
  // session, so it nudges again next visit without nagging every navigation.
  const [classSubjectsPromptDismissed, setClassSubjectsPromptDismissed] =
    useState(() => {
      try {
        return sessionStorage.getItem('classSubjectsPromptDismissed') === '1';
      } catch (e) {
        return false;
      }
    });
  const dismissClassSubjectsPrompt = () => {
    try {
      sessionStorage.setItem('classSubjectsPromptDismissed', '1');
    } catch (e) {
      /* storage unavailable */
    }
    setClassSubjectsPromptDismissed(true);
  };

  // Permission flags for route guards - calling functions directly

  // Determine first accessible tab for fallback
  const getFirstAccessibleRoute = () => {
    // Always try Tasks first for both teachers and students
    if (canAccessTasks(profile)) {
      return '/tasks';
    }
    // Fallback to documents (JeddAI) which is always available
    return '/getFeedback';
  };

  const firstAccessibleRoute = getFirstAccessibleRoute();
  // Subject used in the student plans heading ("Start boosting your <X> marks
  // today"). Falls back to English (matches the Figma copy) when the profile
  // has no subject yet.
  const studentPlansSubject =
    (Array.isArray(profile?.subjects) &&
      (profile.subjects[0]?.subjectName || profile.subjects[0]?.name)) ||
    'English';
  const shouldRequireMyDraftsState =
    shouldRequireMyDraftsStateSelection(profile);
  const shouldRequireNonSchoolTeacherStateYear =
    shouldRequireNonSchoolTeacherStateYearSelection(profile);

  const renderDraftsPage = (Component, props = {}) => {
    if (shouldRequireNonSchoolTeacherStateYear) {
      return (
        <OnboardingScreen
          editStateYear={true}
          onClose={async () => {
            await refetchProfile?.();
          }}
          showCountry={true}
          showYear={false}
          showTerms={false}
          showCloseButton={false}
          disableStateDefault={true}
          headerText="Update your Info"
          submitLabel="Continue"
        />
      );
    }

    if (shouldRequireMyDraftsState) {
      return (
        <OnboardingScreen
          editStateYear={true}
          onClose={async () => {
            await refetchProfile?.();
          }}
          showCountry={false}
          showYear={false}
          showTerms={false}
          showCloseButton={false}
          disableStateDefault={true}
          headerText="Select your State"
          submitLabel="Continue"
        />
      );
    }

    return <Component {...props} />;
  };

  // The phone upload screen stands alone: no router, sidebar or app chrome.
  // It returns BEFORE ddRum() on purpose — RUM records the page URL, and this
  // URL carries the upload token. (Every hook above has already run.)
  if (phoneUploadToken) {
    return (
      <React.Suspense fallback={<Loader />}>
        <MobilePhoneUpload token={phoneUploadToken} />
      </React.Suspense>
    );
  }

  // Initialise Datadog RUM before any early return so the loader,
  // ConnectionBlocked, and the embedded "relaunch from your LMS" states are all
  // observable (incl. a framed launch that ends on the relaunch screen). ddRum()
  // is idempotent — the @datadog/browser-rum init no-ops if already initialised.
  ddRum();

  if (
    !isPublicPath &&
    (!isAuthBootstrapDone ||
      (isAuthenticated && (isLoadingProfile || isLoadingClasses)))
  ) {
    // Branded Lottie loader instead of the shimmer skeleton — users were
    // seeing the generic shimmer block for a second before the branded
    // "Loading brain power..." Loader appeared inside route components.
    return <Loader />;
  }

  if (embeddedExpired) {
    return (
      <div
        role="alert"
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          padding: '2rem',
          textAlign: 'center',
        }}
      >
        <h2>Your session has expired</h2>
        <p>
          Please relaunch JeddAI from your learning management system to
          continue.
        </p>
      </div>
    );
  }

  if (!isPublicPath && !isAuthenticated && authBootstrapError) {
    // Diagnostic context kept in the console for support — the user-facing
    // page (ConnectionBlocked) explains the firewall/network cause instead.
    console.error('JeddAI connection/auth bootstrap failed', {
      authBootstrapError,
      apiBaseUrl,
      isAuthBootstrapDone,
    });
    return (
      <ConnectionBlocked
        apiBaseUrl={apiBaseUrl}
        errorDetail={authBootstrapError}
      />
    );
  }

  // const middleware = (c) => withOnboarding(c);
  const middleware = (c) => c;
  const ProtectedTeacherClassesRoot = middleware(TeacherClassesRoot);
  const ProtectedTaskDetail = middleware(TaskDetail);
  const ProtectedCreateNewTask = middleware(CreateNewTask);
  const ProtectedFeedbacksRoot = middleware(FeedbacksRoot);
  const ProtectedSubmissionViewWrapper = middleware(SubmissionViewWrapper);
  const ProtectedTasksStudentUploadRedirect = middleware(TasksStudentUploadRedirect);
  const ProtectedDocumentRoot = middleware(FeedbacksRoot);
  const ProtectedExemplarResponsesPage = middleware(ExemplarResponsesPage);
  const ProtectedMarkingCriteria = middleware(CreateNewMarkingCriteriaRoot);
  const ProtectedSettings = middleware(AccountSettingsRoot);
  const ProtectedDashboard = middleware(Dashboard);
  const ProtectedTasks = middleware(Tasks);
  const ProtectedFeedbackProvider = middleware(FeedbackProvider);
  const ProtectedFeedbackHistory = middleware(FeedbackHistory);
  const ProtectedDocRoot = middleware(NewDocPage);
  const ProtectedDraftWizard = middleware(DraftWizardPage);
  const ProtectedCompletedRoot = middleware(CompletedPage);

  const ProtectedCommentbanks = middleware(CommentBanks);
  const ProtectedSuccessCriteria = middleware(SuccessCriteria);
  const ProtectedSharedTasks = middleware(SharedTasks);
  const ProtectedSharedMarkingTools = middleware(SharedMarkingTools);
  const ProtectedAdvancedMarkingControls = middleware(AdvancedMarkingControls);

  return (
    <StateSelectionContext.Provider value={stateSelectionContextValue}>
      <AppContext.Provider
        value={{
          setShowStudentOnboarding,
          setShowTeacherOnboarding,
          showWelcomeOnboarding,
          setShowWelcomeOnboarding,
          closeWelcomeOnboarding,
          startSimpleOnboarding,
          activateSidebarOverlay,
          deactivateSidebarOverlay,
          isSidebarOverlayActive,
        }}
      >
        <Router>
          {/* Router v5 accepts a single child — the fragment mounts TourGuard
            (renders nothing; bounces navigation + shows TourExitWarning while
            a tour or the checklist is active) alongside the route Switch. */}
          <>
            <DesktopRouteCanonicalizer mobileView={mobileView} />
            <TourGuard suspended={onboardingSuspended} />
            <SubmissionMaintenanceNotice />
            <StagingModelPicker profile={profile} />
            <Switch>
              <Route path="/teacher-onboarding">
                {/* School-teacher welcome carousel. New users (profile.createdAt) OR
                a Help-Centre relaunch (tourRelaunchRequested) qualify; everyone
                else is redirected. Non-school teachers use the simplified flow. */}
                {canShowOnboardingSurface ? (
                  <TeacherOnboarding
                    onCloseOnboarding={closeSchoolTeacherOnboarding}
                  />
                ) : (
                  <Redirect to="/" />
                )}
              </Route>
              <Route path="/student-onboarding">
                {isNonSchoolStudentRole(profile) ? (
                  <OnboardingScreen
                    editStateYear={false}
                    enableGtmTracking={showStudentOnboarding}
                    onClose={closeOnboarding}
                  />
                ) : (
                  <Redirect to="/" />
                )}
              </Route>
              <Route path="/non-school-teacher-onboarding">
                {isNonSchoolTeacherRole(profile) ? (
                  <OnboardingScreen
                    editStateYear={false}
                    showYear={false}
                    showUsageGoals={true}
                    enableGtmTracking={showNonSchoolTeacherOnboarding}
                    onClose={closeNonSchoolTeacherOnboarding}
                  />
                ) : (
                  <Redirect to="/" />
                )}
              </Route>
              <Route>
                <div className="app-container">
                  {/* The desktop sidebar must not mount when the mobile shell
                      has taken over. It was rendering as an invisible sibling
                      (mobile.css covers it at z-index 5000) with its hooks
                      still running, and its route guard
                      (MainSidebar/index.jsx:296) steered the mobile HashRouter:
                      a JeddAI-locked account following a /documents/ link was
                      pushed to getFirstAccessibleTab(), which resolves to '/' —
                      the neutral Dashboard on desktop, but My Drafts on mobile.
                      It also dragged the sidebar's document/task prefetch onto
                      mobile data.

                      The condition is the SAME one MobileGate uses to take
                      over, so the two can never disagree: desktop (mobileView
                      false) and flag-off mobile are both provably unchanged. */}
                  {shouldShowSidebar(isPublicPath, isAuthenticated) &&
                    !(mobileView && shellEnabled) &&
                    (process.env.REACT_APP_TEACHER_LOOP === 'on' ? (
                      <HideOnTeacherCheckRoute>
                        <SidebarSwitcher
                          isSchoolTeacherProfile={isSchoolTeacherProfile}
                        />
                      </HideOnTeacherCheckRoute>
                    ) : (
                      <SidebarSwitcher
                        isSchoolTeacherProfile={isSchoolTeacherProfile}
                      />
                    ))}

                  {isTeacherOnboarding(
                    showTeacherOnboarding,
                    mobileView,
                    role
                  ) &&
                    // School teachers only: NEW users (profile.createdAt) OR a
                    // Help-Centre relaunch (tourRelaunchRequested) render the
                    // welcome carousel inline — both covered by
                    // canShowOnboardingSurface. Non-school teachers never set
                    // showTeacherOnboarding (they use the simplified flow), so the
                    // redirect branch is only a defensive fallback.
                    (canShowOnboardingSurface ? (
                      <TeacherOnboarding
                        onCloseOnboarding={closeSchoolTeacherOnboarding}
                      />
                    ) : (
                      <Redirect to="/teacher-onboarding" />
                    ))}
                  {!isSchoolTeacherProfile &&
                    isShowWelcomeOnboarding(
                      showWelcomeOnboarding,
                      mobileView,
                      role
                    ) && <WelcomeOnboarding profile={profile} />}

                  {/* Embedded-LTI teachers must declare subject/year for LMS-provisioned classes
                  (the LMS sends neither) before using the app — blocking by design. */}
                  {isAuthenticated &&
                    isTeacher &&
                    isEmbeddedMode() &&
                    !isLoadingClasses &&
                    ltiClassesMissingSubjects.length > 0 &&
                    intoShell(
                      <ClassSubjectsModal
                        classes={ltiClassesMissingSubjects}
                        onSaved={refetchClasses}
                      />
                    )}

                  {/* US school teachers: unmapped classes silently match zero state
                  tools (default falls back to AllEnglish), so prompt for class
                  subjects — dismissible, once per session, never for AU. */}
                  {isAuthenticated &&
                    isTeacher &&
                    !isEmbeddedMode() &&
                    !isLoadingClasses &&
                    isUsCountry(profile?.country) &&
                    !classSubjectsPromptDismissed &&
                    !isShowWelcomeOnboarding(
                      showWelcomeOnboarding,
                      mobileView,
                      role
                    ) &&
                    ltiClassesMissingSubjects.length > 0 &&
                    intoShell(
                      <ClassSubjectsModal
                        classes={ltiClassesMissingSubjects}
                        onSaved={refetchClasses}
                        onDismiss={dismissClassSubjectsPrompt}
                      />
                    )}

                  {isStudentOnboarding(showStudentOnboarding) && (
                    <Redirect to="/student-onboarding" />
                  )}
                  {isNewSchoolTeacher &&
                    showSessionBooking &&
                    (mobileView ? (
                      <WelcomeOverlayMobile />
                    ) : (
                      <SessionBookingPopup
                        menuItems={[
                          'I have booked a session',
                          'Someone else from my faculty has booked a session',
                          'My faculty has already completed the session',
                        ]}
                        onClose={closeSessionBooking}
                        handlePreviousButtonClick={() => {
                          closeSessionBooking();
                        }}
                        onSubmit={async (selected) => {
                          // Only tick + advance after a successful save (the row
                          // must not show a green check the backend never stored).
                          try {
                            await saveSessionBooking(selected);
                          } catch (e) {
                            console.error('Failed to save session booking', e);
                            toast("Couldn't save that — please try again.");
                            return;
                          }
                          setOnboardingTasks((prev) => {
                            const updated = [...prev];
                            updated[0] = true;
                            return updated;
                          });
                          closeSessionBooking();
                          if (onboardingTasks && !onboardingTasks[1]) {
                            setTimeout(
                              () => setShowVerifyClassesPopup(true),
                              50
                            );
                          }
                        }}
                      />
                    ))}
                  {isSchoolTeacherProfile &&
                    showVerifyClassesPopup &&
                    !inMobileShell && (
                      <VerifyClassesPopup
                        setShowVerifyClassesPopup={setShowVerifyClassesPopup}
                        onOpenClassChangeRequest={() =>
                          setShowClassChangeRequest(true)
                        }
                        setShowMarkingPreferencesPopup={(val) => {
                          if (val && onboardingTasks && onboardingTasks[2])
                            return;
                          setShowMarkingPreferencesPopup(val);
                        }}
                        onConfirm={() => {
                          setOnboardingTasks((prev) => {
                            const updated = [...prev];
                            updated[1] = true;
                            return updated;
                          });
                        }}
                      />
                    )}
                  {isSchoolTeacherProfile &&
                    showClassChangeRequest &&
                    !inMobileShell && (
                      <ClassChangeRequestPopup
                        onClose={() => setShowClassChangeRequest(false)}
                      />
                    )}
                  {isSchoolTeacherProfile &&
                    showMarkingPreferencesPopup &&
                    !inMobileShell && (
                      <MarkingPreferencesPopup
                        setShowMarkingPreferencesPopup={
                          setShowMarkingPreferencesPopup
                        }
                        onSave={() => {
                          // Only flip the marking-prefs index (2). Previously also
                          // flipped 0 (book session) and 1 (verify classes), which
                          // was wrong — saving prefs doesn't mean the user
                          // booked or verified anything.
                          setOnboardingTasks((prev) => {
                            const newTasks = [...prev];
                            newTasks[2] = true;
                            return newTasks;
                          });
                        }}
                      />
                    )}
                  {showNonSchoolTeacherOnboarding &&
                    (process.env.REACT_APP_TEACHER_LOOP === 'on' ? (
                      // Teacher Loop: a teacher mid-onboarding who opens a
                      // Teacher Check, thank-you card or confirmation link
                      // stays on it; onboarding resumes on the next page.
                      <HideOnTeacherCheckRoute>
                        <Redirect to="/non-school-teacher-onboarding" />
                      </HideOnTeacherCheckRoute>
                    ) : (
                      <Redirect to="/non-school-teacher-onboarding" />
                    ))}
                  {/* Simplified onboarding overlays (non-school teacher + students). */}
                  {showSimpleCarousel && simpleAudience && (
                    <SimpleOnboardingCarousel
                      audience={
                        simpleAudience === 'nonSchoolTeacher'
                          ? 'teacher'
                          : 'student'
                      }
                      onFinish={handleSimpleCarouselFinish}
                    />
                  )}
                  {showSimplePlans && simpleAudience === 'nonSchoolTeacher' && (
                    <TeacherPlanUpgradePopup
                      isOpen
                      showAllPlans
                      currentPlan="free"
                      profile={profile}
                      eyebrow="Plans"
                      title="Reduce your workload with AI marking"
                      subtitle="Supercharge your teaching with personalised AI marking and feedback tools"
                      onClose={finishSimpleOnboarding}
                      onContinueFree={finishSimpleOnboarding}
                    />
                  )}
                  {showSimplePlans && simpleAudience === 'nonSchoolStudent' && (
                    <StudentPlanUpgradePopup
                      isOpen
                      showAllPlans
                      currentPlan="free"
                      profile={profile}
                      eyebrow="Plans"
                      title={`Start boosting your ${studentPlansSubject} marks today`}
                      subtitle="Improve your marks today by getting instant feedback and grades for your work"
                      onClose={finishSimpleOnboarding}
                      onContinueFree={finishSimpleOnboarding}
                    />
                  )}
                  <VisibilityWrapper>
                    {canShowOnboardingSurface && showOnboardingComplete && !onboardingSuspended && (
                      <OnboardingComplete
                        onClose={handleCloseOnboardingComplete}
                      />
                    )}

                    {canShowOnboardingSurface && showDraftFeedback && !onboardingSuspended && (
                      <CustomizeDraftFeedback
                        onClose={() => setShowDraftFeedback(false)}
                        onCreateDraft={() => {
                          setShowDraftFeedback(false);
                        }}
                      />
                    )}

                    {/* Mobile overlay: only show if mobile view AND any of the modals is active */}
                    {mobileView &&
                      canShowOnboardingSurface &&
                      !onboardingSuspended &&
                      (showOnboardingComplete || showDraftFeedback) && (
                        <WelcomeOverlayMobile />
                      )}
                    {mobileView && !responsivePublicRoute ? (
                      // Mobile experience (flag-gated). With the flag off,
                      // AppMobileGate renders its child — the original
                      // WelcomeOverlayMobile wall — so mobile behaves exactly
                      // as before. App's held mobile decision is passed through
                      // unchanged, so crossing 765px mid-form cannot unmount it.
                      <AppMobileGate
                        mobileExperienceEnabled={shellEnabled}
                        publicOnly={mobilePublicOnly}
                        profile={profile}
                        role={role}
                      >
                        <WelcomeOverlayMobile />
                      </AppMobileGate>
                    ) : (
                      <React.Suspense fallback={<Loader />}>
                        <Switch>
                          <Route
                            path="/docs"
                            render={() => renderDraftsPage(ProtectedDocRoot)}
                          />
                          <Route
                            path="/tools/markingcriterias"
                            render={() => {
                              if (!canAccessMarkingTools(profile)) {
                                return <Redirect to={firstAccessibleRoute} />;
                              }
                              return <ProtectedSettings />;
                            }}
                          />
                          <Route
                            exact
                            path="/tools/rubrics"
                            render={() => {
                              if (!canAccessMarkingTools(profile)) {
                                return <Redirect to={firstAccessibleRoute} />;
                              }
                              return <ProtectedSettings />;
                            }}
                          />
                          <Route
                            path="/tools/rubrics/:markingCriteriaId"
                            render={() => {
                              if (!canAccessMarkingTools(profile)) {
                                return <Redirect to={firstAccessibleRoute} />;
                              }
                              return <ProtectedMarkingCriteria />;
                            }}
                          />
                          <Route
                            path="/getFeedback"
                            render={() => renderDraftsPage(ProtectedDocRoot)}
                          />
                          <Route path="/feedbackRequest/documentsReview/:id">
                            <ProtectedDocumentRoot />
                          </Route>
                          <Route
                            path="/giveFeedback"
                            render={() => {
                              if (!canViewFeedbackRequests(profile)) {
                                return <Redirect to={firstAccessibleRoute} />;
                              }
                              return <ProtectedFeedbackProvider />;
                            }}
                          />
                          <Route
                            path="/completed"
                            render={() => {
                              if (!canViewClosedTasks(profile)) {
                                return <Redirect to={firstAccessibleRoute} />;
                              }
                              return <ProtectedCompletedRoot />;
                            }}
                          />
                          <Route path="/feedbackHistory/documentsReview/:id">
                            <ProtectedDocumentRoot />
                          </Route>
                          <Route
                            path="/feedbackHistory"
                            render={() => {
                              if (!canViewFeedbackHistory(profile)) {
                                return <Redirect to={firstAccessibleRoute} />;
                              }
                              return <ProtectedFeedbackHistory />;
                            }}
                          />
                          <Route
                            exact
                            path="/class-insights"
                            render={() => <Redirect to="/classes-insights" />}
                          />
                          <Route
                            path="/classes-insights"
                            render={() => {
                              if (!canAccessInsights(profile)) {
                                return <Redirect to={firstAccessibleRoute} />;
                              }
                              return <ClassInsightsPage />;
                            }}
                          />
                          <Route
                            path="/classes/:classIdFromUrl?"
                            render={() => {
                              if (!canAccessInsights(profile)) {
                                return <Redirect to={firstAccessibleRoute} />;
                              }
                              return <ProtectedTeacherClassesRoot />;
                            }}
                          />
                          <Route
                            exact
                            path="/tasks/:assignmentId/upload/:studentId"
                            render={() => {
                              if (!canViewCurrentTasks(profile)) {
                                return <Redirect to={firstAccessibleRoute} />;
                              }
                              return <ProtectedTasksStudentUploadRedirect />;
                            }}
                          />
                          <Route
                            exact
                            path="/tasks/:assignmentId/start"
                            render={(props) => {
                              if (!canViewCurrentTasks(profile)) {
                                return <Redirect to={firstAccessibleRoute} />;
                              }
                              return isTeacher ? (
                                <Redirect
                                  to={`/tasks/${props.match.params.assignmentId}`}
                                />
                              ) : (
                                <ProtectedTaskDetail />
                              );
                            }}
                          />
                          <Route
                            exact
                            path="/tasks/:assignmentId"
                            render={(props) => {
                              const assignmentId =
                                props.match.params.assignmentId;
                              const isCreateTaskRoute = assignmentId === 'new';

                              if (
                                isCreateTaskRoute &&
                                !canCreateTask(profile)
                              ) {
                                return <Redirect to={firstAccessibleRoute} />;
                              }

                              if (
                                !isCreateTaskRoute &&
                                !canViewCurrentTasks(profile) &&
                                !canViewClosedTasks(profile)
                              ) {
                                return <Redirect to={firstAccessibleRoute} />;
                              }
                              return isTeacher ? (
                                <ProtectedCreateNewTask />
                              ) : (
                                <Redirect
                                  to={`/tasks/${props.match.params.assignmentId}/start`}
                                />
                              );
                            }}
                          />
                          <Route
                            path="/tasks"
                            render={() => {
                              if (!canViewCurrentTasks(profile)) {
                                return <Redirect to={firstAccessibleRoute} />;
                              }
                              return <ProtectedTasks role={role} />;
                            }}
                          />
                          <Route
                            path="/sharedresponses"
                            render={() => {
                              if (!canViewSharedResponses(profile)) {
                                return <Redirect to={firstAccessibleRoute} />;
                              }
                              return <ProtectedExemplarResponsesPage />;
                            }}
                          />
                          <Route
                            path="/sharedtasks"
                            render={() => {
                              if (!canViewSharedResponses(profile)) {
                                return <Redirect to={firstAccessibleRoute} />;
                              }
                              return <ProtectedSharedTasks />;
                            }}
                          />
                          <Route
                            path="/sharedmarkingtools"
                            render={() => {
                              if (!canAccessMarkingTools(profile)) {
                                return <Redirect to={firstAccessibleRoute} />;
                              }
                              return <ProtectedSharedMarkingTools />;
                            }}
                          />
                          <Route
                            path="/advanced-marking"
                            render={() => (
                              <Redirect to="/tools/advanced-controls" />
                            )}
                          />
                          <Route
                            path="/tools/advanced-marking"
                            render={() => (
                              <Redirect to="/tools/advanced-controls" />
                            )}
                          />
                          {process.env.REACT_APP_TEACHER_LOOP === 'on' && (
                            <Route path="/check/verify">
                              <TeacherCheckVerifyPage />
                            </Route>
                          )}
                          {process.env.REACT_APP_TEACHER_LOOP === 'on' && (
                            <Route path="/check/:token">
                              <TeacherCheckPage />
                            </Route>
                          )}
                          {process.env.REACT_APP_TEACHER_LOOP === 'on' && (
                            <Route path="/thanks/:code">
                              <GiftCardPage />
                            </Route>
                          )}
                          {process.env.REACT_APP_TEACHER_LOOP === 'on' && (
                            <Route path="/gift/verify">
                              <GiftVerifyPage />
                            </Route>
                          )}
                          <Route path="/view/submissions/:id">
                            <ProtectedFeedbacksRoot />
                          </Route>
                          {/* Upload-on-behalf is a mode of the feedback page,
                              not a page of its own — FeedbacksRoot switches on
                              this path. */}
                          <Route path="/submissions/:id/upload-response">
                            <ProtectedFeedbacksRoot />
                          </Route>
                          <Route
                            path="/submissions/:id/review"
                            render={({ match, location: routeLocation }) => {
                              const params = new URLSearchParams(
                                routeLocation.search || '',
                              );
                              if (params.get('autoUpload') === 'true') {
                                const question = params.get('question');
                                const nextSearch = question
                                  ? `?question=${encodeURIComponent(question)}`
                                  : '';
                                return (
                                  <Redirect
                                    to={`/submissions/${match.params.id}/upload-response${nextSearch}`}
                                  />
                                );
                              }
                              return (
                                <ProtectedFeedbacksRoot isAssignmentPage={false} />
                              );
                            }}
                          />
                          <Route exact path="/submissions/:id">
                            <ProtectedSubmissionViewWrapper />
                          </Route>
                          <Route
                            path="/docs"
                            render={() =>
                              renderDraftsPage(ProtectedDocumentRoot)
                            }
                          />
                          <Route
                            path="/documents/:id"
                            render={() =>
                              renderDraftsPage(ProtectedDocumentRoot)
                            }
                          />
                          {/* A bulk draft: one assignment, many submissions, so
                              there is no single document id to address it by. */}
                          <Route
                            path="/drafts/:assignmentId"
                            render={() =>
                              renderDraftsPage(ProtectedDraftWizard)
                            }
                          />
                          <Route path="/documentsReview/:id">
                            <ProtectedDocumentRoot />
                          </Route>
                          <Route
                            exact
                            path="/tools/commentbanks/new"
                            render={() => {
                              if (!canAccessMarkingTools(profile)) {
                                return <Redirect to={firstAccessibleRoute} />;
                              }
                              return <CreateNewCommentBankRoot />;
                            }}
                          />
                          <Route
                            path="/tools/commentbanks/:commentBankId"
                            render={() => {
                              if (!canAccessMarkingTools(profile)) {
                                return <Redirect to={firstAccessibleRoute} />;
                              }
                              return <CommentBankFeedbackAreas />;
                            }}
                          />
                          <Route
                            path="/tools/commentbanks"
                            render={() => {
                              if (!canAccessMarkingTools(profile)) {
                                return <Redirect to={firstAccessibleRoute} />;
                              }
                              return <ProtectedCommentbanks />;
                            }}
                          />
                          <Route
                            exact
                            path="/tools/successcriterias/new"
                            render={() => {
                              if (!canAccessMarkingTools(profile)) {
                                return <Redirect to={firstAccessibleRoute} />;
                              }
                              return <CreateNewSuccessCriteria />;
                            }}
                          />
                          <Route
                            path="/tools/successcriterias/:successCriteriaId"
                            render={() => {
                              if (!canAccessMarkingTools(profile)) {
                                return <Redirect to={firstAccessibleRoute} />;
                              }
                              return <SuccessCriteriaDetail />;
                            }}
                          />
                          <Route
                            exact
                            path="/tools/successcriterias"
                            render={() => {
                              if (!canAccessMarkingTools(profile)) {
                                return <Redirect to={firstAccessibleRoute} />;
                              }
                              return <ProtectedSuccessCriteria />;
                            }}
                          />
                          <Route
                            exact
                            path="/tools/shortanswers/new"
                            render={() => {
                              if (!canAccessMarkingTools(profile)) {
                                return <Redirect to={firstAccessibleRoute} />;
                              }
                              return <CreateNewShortAnswer />;
                            }}
                          />
                          <Route
                            path="/tools/shortanswers/:shortAnswerId"
                            render={() => {
                              if (!canAccessMarkingTools(profile)) {
                                return <Redirect to={firstAccessibleRoute} />;
                              }
                              return <ShortAnswerDetail />;
                            }}
                          />
                          <Route
                            exact
                            path="/tools/shortanswers"
                            render={() => {
                              if (!canAccessMarkingTools(profile)) {
                                return <Redirect to={firstAccessibleRoute} />;
                              }
                              return <ShortAnswers />;
                            }}
                          />
                          <Route
                            path="/tools/advanced-controls"
                            render={() => {
                              if (!canAccessMarkingTools(profile)) {
                                return <Redirect to={firstAccessibleRoute} />;
                              }
                              return <ProtectedAdvancedMarkingControls />;
                            }}
                          />
                          <Route
                            path="/jeddai"
                            render={() => {
                              if (!canAccessMarkingTools(profile)) {
                                return <Redirect to={firstAccessibleRoute} />;
                              }
                              return <Redirect to="/tools/advanced-controls" />;
                            }}
                          />
                          {/*
                      Onboarding mirror routes — rendered ONLY during the
                      teacher onboarding tours. They use parallel components
                      in components-onboarding/ so production pages stay
                      tour-free. Guard: only school-teacher profiles.
                    */}
                          <Route
                            exact
                            path="/onboarding/marking"
                            render={() => {
                              if (!canShowOnboardingSurface) {
                                return <Redirect to={firstAccessibleRoute} />;
                              }
                              return (
                                <OnboardingPage>
                                  <OnboardingMarkingPage />
                                </OnboardingPage>
                              );
                            }}
                          />
                          {/*
                      Marking-tour sub-tool routes. The tour navigates
                      between these at steps 4-6 (Comment Banks → Success
                      Criteria → Short Answers) and back to /onboarding/marking
                      at step 7. SecondSidebar's handleNext drives the
                      navigation; TourGuard keeps the user inside /onboarding/*.
                    */}
                          <Route
                            exact
                            path="/onboarding/tools/commentbanks"
                            render={() => {
                              if (!canShowOnboardingSurface) {
                                return <Redirect to={firstAccessibleRoute} />;
                              }
                              return (
                                <OnboardingPage>
                                  <OnboardingCommentBanks />
                                </OnboardingPage>
                              );
                            }}
                          />
                          <Route
                            exact
                            path="/onboarding/tools/successcriterias"
                            render={() => {
                              if (!canShowOnboardingSurface) {
                                return <Redirect to={firstAccessibleRoute} />;
                              }
                              return (
                                <OnboardingPage>
                                  <OnboardingSuccessCriteria />
                                </OnboardingPage>
                              );
                            }}
                          />
                          <Route
                            exact
                            path="/onboarding/tools/shortanswers"
                            render={() => {
                              if (!canShowOnboardingSurface) {
                                return <Redirect to={firstAccessibleRoute} />;
                              }
                              return (
                                <OnboardingPage>
                                  <OnboardingShortAnswers />
                                </OnboardingPage>
                              );
                            }}
                          />
                          <Route
                            exact
                            path="/onboarding/new-task"
                            render={() => {
                              if (!canShowOnboardingSurface) {
                                return <Redirect to={firstAccessibleRoute} />;
                              }
                              return (
                                <OnboardingPage>
                                  <OnboardingCreateNewTask />
                                </OnboardingPage>
                              );
                            }}
                          />
                          {/*
                      Both `/onboarding/documents/:id` and
                      `/onboarding/submissions/:id` render OnboardingFeedbacksRoot.
                      The mirror FeedbacksRoot reads :id from the URL and
                      switches between demo-draft and demo-onboarding demo
                      content based on the id. OnboardingNewDocRoot was the
                      landing page; for the tour we go straight to the demo
                      editor.
                    */}
                          <Route
                            path="/onboarding/documents/:id"
                            render={() => {
                              if (
                                !canShowOnboardingSurface &&
                                !canShowWizardDemo
                              ) {
                                return <Redirect to={firstAccessibleRoute} />;
                              }
                              return (
                                <OnboardingPage>
                                  <OnboardingFeedbacksRoot />
                                </OnboardingPage>
                              );
                            }}
                          />
                          <Route
                            path="/onboarding/submissions/:id"
                            render={() => {
                              if (!canShowOnboardingSurface) {
                                return <Redirect to={firstAccessibleRoute} />;
                              }
                              return (
                                <OnboardingPage>
                                  <OnboardingFeedbacksRoot />
                                </OnboardingPage>
                              );
                            }}
                          />
                          <Route path="/404">
                            <PageNotFound />
                          </Route>
                          <Route exact path="/">
                            <ProtectedDashboard role={role} />
                          </Route>
                          <Redirect to="/404" />
                        </Switch>
                      </React.Suspense>
                    )}
                  </VisibilityWrapper>
                  {/* Inert dim layer — the old onClick deactivated the marking
                    and new-task tours (not even the draft tour it renders
                    under). Clicking outside the spotlight must not change
                    tour state. */}
                  {(draftTourActive || wizardTourActive) && <TourOverlay />}
                </div>
              </Route>
            </Switch>
            {process.env.REACT_APP_TEACHER_LOOP === 'on' && isAuthenticated && (
              <React.Suspense fallback={null}>
                <TeacherLoopPendingClaims role={role} />
              </React.Suspense>
            )}
            {process.env.REACT_APP_TEACHER_LOOP === 'on' && isAuthenticated && (
              <React.Suspense fallback={null}>
                <TeacherLoopPendingGift
                  role={role}
                  profile={profile}
                  aboveMobileShell={mobileView}
                  blocked={
                    showTeacherOnboarding ||
                    teacherLoopOnboardingWaiting ||
                    showWelcomeOnboarding ||
                    showNonSchoolTeacherOnboarding ||
                    showStateSelectionModal ||
                    // Every other onboarding overlay, under the condition
                    // that renders it (JED-1464). The simplified flow:
                    // welcome carousel → demo-draft wizard → plans.
                    (Boolean(simpleAudience) &&
                      (showSimpleCarousel || showSimplePlans)) ||
                    wizardCompleted ||
                    markingTourActive ||
                    draftTourActive ||
                    newTaskTourActive ||
                    feedbackTourActive ||
                    wizardTourActive ||
                    // The school teacher's setup checklist and its popups.
                    (canShowOnboardingSurface &&
                      !onboardingSuspended &&
                      (showOnboardingComplete || showDraftFeedback)) ||
                    (isNewSchoolTeacher && showSessionBooking) ||
                    (isSchoolTeacherProfile &&
                      !inMobileShell &&
                      (showVerifyClassesPopup || showMarkingPreferencesPopup))
                  }
                />
              </React.Suspense>
            )}
            <ToastContainer
              position="bottom-left"
              autoClose={6000}
              hideProgressBar={true}
              newestOnTop={false}
              closeOnClick
              rtl={false}
              pauseOnFocusLoss
              draggable={false}
              pauseOnHover
              theme="dark"
            />
          </>
        </Router>
      </AppContext.Provider>
      {showStateSelectionModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 9999,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <OnboardingScreen
            editStateYear={true}
            onClose={async () => {
              setShowStateSelectionModal(false);
              await refetchProfile?.();
            }}
            showCountry={true}
            showYear={isNonSchoolStudentRole(profile) && !profile?.year}
            showTerms={false}
            showCloseButton={false}
            disableStateDefault={true}
            headerText="Select your Location"
            submitLabel="Continue"
            menuZIndex={10001}
          />
        </div>
      )}
    </StateSelectionContext.Provider>
  );
}
export default App;

const TourOverlay = styled.div`
  position: fixed;
  inset: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.3);
  z-index: 1000;
  pointer-events: none;
`;
